import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { adminFetch } from '../../utils/adminApi';
import {
  Plus, Trash2, Edit, Save, X, RefreshCw, Copy, Image as ImageIcon, Droplets, Upload
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ACCENT_OPTIONS = [
  { value: '#0ea5e9', label: 'Sky / Cyan' },
  { value: '#0891b2', label: 'Teal' },
  { value: '#16a34a', label: 'Green' },
  { value: '#2563eb', label: 'Blue' },
  { value: '#7c3aed', label: 'Violet' },
];

const blank = () => ({
  slug: '', name: '', brand: 'Willfread', tag: 'STARTER', category: 'home',
  price: 0, deposit: 0, buyback: 0, lockIn: '3 Months', buybackAfter: '6 Months',
  capacity: '', suitable: '', tech: '',
  features: [], freeServices: ['Free Installation', '₹0 Maintenance', 'Free Filter Replacement'],
  images: [], accent_color: '#16a34a', display_order: 0, is_active: true,
});

const input = "w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm";

function AdminRentModels() {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showNew, setShowNew] = useState(false);

  const fetchModels = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/rent-models`);
      if (res.ok) setModels(await res.json());
    } catch { toast.error('Failed to load rent models'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchModels(); }, [fetchModels]);

  const createModel = async (draft) => {
    if (!draft.slug || !draft.name) { toast.error('Slug and Name required'); return; }
    const slug = draft.slug.trim().toLowerCase().replace(/[^a-z0-9-]/g, '-');
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/rent-models`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...draft, slug })
      });
      if (res.ok) { toast.success('Model created'); setShowNew(false); fetchModels(); }
      else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
    } catch { toast.error('Create failed'); }
  };

  const deleteModel = async (slug) => {
    if (!confirm(`Delete model "${slug}"?`)) return;
    const res = await adminFetch(`${BACKEND_URL}/api/admin/rent-models/${slug}`, { method: 'DELETE' });
    if (res.ok) { toast.success('Deleted'); fetchModels(); } else toast.error('Delete failed');
  };

  return (
    <div data-testid="admin-rent-models">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">RO on Rent — Models</h1>
          <p className="text-gray-500 text-sm">Manage the RO models shown on the /ro-on-rent page</p>
        </div>
        <button onClick={() => setShowNew(true)} data-testid="add-rent-model-btn"
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium">
          <Plus size={16} /> Add Model
        </button>
      </div>

      {loading ? (
        <div className="bg-white rounded-xl p-12 flex justify-center"><RefreshCw className="w-6 h-6 text-blue-500 animate-spin" /></div>
      ) : models.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center text-gray-500">
          <Droplets className="w-12 h-12 mx-auto mb-2 text-gray-300" />
          No rent models yet. Click &quot;Add Model&quot;.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {models.map(m => (
            <div key={m.slug} data-testid={`rent-card-${m.slug}`}
              className="bg-white rounded-xl shadow-sm p-4 border-2 border-gray-100 hover:border-blue-200 transition">
              {m.images?.[0] ? (
                <img src={m.images[0]} alt={m.name} className="w-full h-32 object-contain bg-gray-50 rounded mb-3" />
              ) : (
                <div className="w-full h-32 bg-gray-50 rounded mb-3 flex items-center justify-center text-gray-300">
                  <ImageIcon size={32} />
                </div>
              )}
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h3 className="font-bold text-gray-900">{m.name}</h3>
                  <p className="text-xs text-gray-400 font-mono">/{m.slug}</p>
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full ${m.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                  {m.is_active ? 'Active' : 'Hidden'}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center text-[11px] text-gray-500 mb-3 pb-2 border-b">
                <div><div className="font-bold text-gray-700">₹{m.price}</div>/mo</div>
                <div><div className="font-bold text-gray-700">{m.images?.length || 0}</div>images</div>
                <div><div className="font-bold text-gray-700">{m.features?.length || 0}</div>features</div>
              </div>
              <div className="flex gap-1.5">
                <button onClick={() => setEditing(m)} data-testid={`edit-rent-${m.slug}`}
                  className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-xs">
                  <Edit size={13} /> Edit
                </button>
                <button onClick={() => deleteModel(m.slug)} data-testid={`del-rent-${m.slug}`}
                  className="px-2 py-1.5 text-red-500 hover:bg-red-50 rounded-lg border border-gray-200">
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showNew && (
        <NewModelModal onClose={() => setShowNew(false)} onCreate={createModel} />
      )}
      {editing && (
        <RentModelEditor model={editing} onClose={() => setEditing(null)} onSaved={() => { fetchModels(); setEditing(null); }} />
      )}
    </div>
  );
}

function NewModelModal({ onClose, onCreate }) {
  const [draft, setDraft] = useState(blank());
  const set = (k, v) => setDraft(p => ({ ...p, [k]: v }));
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">New Rent Model</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-sm font-medium">Slug *</label>
            <input value={draft.slug} onChange={e => set('slug', e.target.value)} placeholder="e.g. premium" className={input} data-testid="new-rent-slug" />
          </div>
          <div>
            <label className="text-sm font-medium">Model Name *</label>
            <input value={draft.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Premium Series" className={input} data-testid="new-rent-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium">Monthly Rent (₹)</label>
              <input type="number" value={draft.price} onChange={e => set('price', parseFloat(e.target.value) || 0)} className={input} />
            </div>
            <div>
              <label className="text-sm font-medium">Category</label>
              <select value={draft.category} onChange={e => set('category', e.target.value)} className={input}>
                <option value="home">Home</option>
                <option value="commercial">Commercial</option>
              </select>
            </div>
          </div>
        </div>
        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="flex-1 px-4 py-2 border rounded-lg">Cancel</button>
          <button onClick={() => onCreate(draft)} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg" data-testid="confirm-create-rent">Create</button>
        </div>
      </div>
    </div>
  );
}

function RentModelEditor({ model, onClose, onSaved }) {
  const [data, setData] = useState({ ...model });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const set = (k, v) => setData(p => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/rent-models/${model.slug}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data)
      });
      if (res.ok) { toast.success('Saved'); onSaved(); } else toast.error('Save failed');
    } catch { toast.error('Save error'); }
    setSaving(false);
  };

  const addImage = () => set('images', [...(data.images || []), '']);
  const updateImage = (i, v) => { const n = [...(data.images || [])]; n[i] = v; set('images', n); };
  const removeImage = (i) => { const n = [...(data.images || [])]; n.splice(i, 1); set('images', n); };

  const uploadFiles = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setUploading(true);
    const uploaded = [];
    try {
      for (const file of Array.from(fileList)) {
        if (!file.type.startsWith('image/')) {
          toast.error(`${file.name} is not an image`);
          continue;
        }
        const fd = new FormData();
        fd.append('file', file);
        fd.append('folder', 'products');
        const res = await fetch(`${BACKEND_URL}/api/upload`, { method: 'POST', body: fd });
        if (res.ok) {
          const rec = await res.json();
          uploaded.push(rec.url);
        } else {
          toast.error(`Failed to upload ${file.name}`);
        }
      }
      if (uploaded.length) {
        set('images', [...(data.images || []), ...uploaded]);
        toast.success(`${uploaded.length} image(s) uploaded`);
      }
    } catch (e) {
      toast.error('Upload error');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const setArrayField = (k, value) => set(k, value.split('\n').map(s => s.trim()).filter(Boolean));

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 overflow-y-auto" data-testid="rent-editor-modal">
      <div className="bg-white rounded-xl my-6 shadow-2xl max-w-3xl w-full flex flex-col" style={{ maxHeight: '94vh' }}>
        <div className="sticky top-0 bg-white border-b px-5 py-3 flex items-center justify-between rounded-t-xl z-10">
          <span className="font-semibold text-gray-800">Edit: {data.name} <span className="text-xs text-gray-400 font-mono ml-2">/{data.slug}</span></span>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-3 py-1.5 border rounded-lg text-sm">Cancel</button>
            <button onClick={save} disabled={saving}
              className="px-4 py-1.5 bg-blue-600 text-white rounded-lg disabled:opacity-50 text-sm flex items-center gap-1.5" data-testid="rent-editor-save">
              {saving ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />} Save
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <div className="grid md:grid-cols-2 gap-3">
            <div><label className="text-sm font-semibold">Model Name</label><input value={data.name || ''} onChange={e => set('name', e.target.value)} className={input} /></div>
            <div><label className="text-sm font-semibold">Brand</label><input value={data.brand || ''} onChange={e => set('brand', e.target.value)} className={input} /></div>
            <div><label className="text-sm font-semibold">Tag (badge text)</label><input value={data.tag || ''} onChange={e => set('tag', e.target.value)} className={input} placeholder="e.g. POPULAR" /></div>
            <div>
              <label className="text-sm font-semibold">Category</label>
              <select value={data.category || 'home'} onChange={e => set('category', e.target.value)} className={input}>
                <option value="home">Home</option>
                <option value="commercial">Commercial</option>
              </select>
            </div>
            <div><label className="text-sm font-semibold">Monthly Rent (₹)</label><input type="number" value={data.price || 0} onChange={e => set('price', parseFloat(e.target.value) || 0)} className={input} /></div>
            <div><label className="text-sm font-semibold">Security Deposit (₹)</label><input type="number" value={data.deposit || 0} onChange={e => set('deposit', parseFloat(e.target.value) || 0)} className={input} /></div>
            <div><label className="text-sm font-semibold">Buy-Back Value (₹)</label><input type="number" value={data.buyback || 0} onChange={e => set('buyback', parseFloat(e.target.value) || 0)} className={input} /></div>
            <div><label className="text-sm font-semibold">Lock-in</label><input value={data.lockIn || ''} onChange={e => set('lockIn', e.target.value)} className={input} placeholder="3 Months" /></div>
            <div><label className="text-sm font-semibold">Buy-Back After</label><input value={data.buybackAfter || ''} onChange={e => set('buybackAfter', e.target.value)} className={input} placeholder="6 Months" /></div>
            <div><label className="text-sm font-semibold">Capacity</label><input value={data.capacity || ''} onChange={e => set('capacity', e.target.value)} className={input} placeholder="12 Liter Storage" /></div>
            <div className="md:col-span-2"><label className="text-sm font-semibold">Suitable For</label><input value={data.suitable || ''} onChange={e => set('suitable', e.target.value)} className={input} placeholder="Best for 6 Adults" /></div>
            <div className="md:col-span-2"><label className="text-sm font-semibold">Technology</label><input value={data.tech || ''} onChange={e => set('tech', e.target.value)} className={input} placeholder="RO + UV + Alkaline + TDS" /></div>
            <div>
              <label className="text-sm font-semibold">Card Accent</label>
              <select value={data.accent_color || '#16a34a'} onChange={e => set('accent_color', e.target.value)} className={input}>
                {ACCENT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div><label className="text-sm font-semibold">Display Order</label><input type="number" value={data.display_order || 0} onChange={e => set('display_order', parseInt(e.target.value) || 0)} className={input} /></div>
          </div>

          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={data.is_active !== false} onChange={e => set('is_active', e.target.checked)} className="w-4 h-4" data-testid="rent-is-active" />
              Active (visible on /ro-on-rent)
            </label>
          </div>

          {/* Features list */}
          <div>
            <label className="text-sm font-semibold">Features (one per line)</label>
            <textarea
              defaultValue={(data.features || []).join('\n')}
              onBlur={e => setArrayField('features', e.target.value)}
              rows={5} className={input + ' resize-y font-mono'}
              data-testid="rent-features"
              placeholder="RO + UV + Alkaline + TDS Technology&#10;12 Liter Storage Tank" />
          </div>

          {/* Free services list */}
          <div>
            <label className="text-sm font-semibold">Free Services (one per line)</label>
            <textarea
              defaultValue={(data.freeServices || []).join('\n')}
              onBlur={e => setArrayField('freeServices', e.target.value)}
              rows={3} className={input + ' resize-y font-mono'}
              placeholder="Free Installation&#10;₹0 Maintenance" />
          </div>

          {/* Images */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-semibold flex items-center gap-1.5">
                <ImageIcon size={14} /> Images ({(data.images || []).length})
              </label>
              <div className="flex items-center gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => uploadFiles(e.target.files)}
                  className="hidden"
                  data-testid="rent-image-file-input"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="flex items-center gap-1 px-2.5 py-1 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  data-testid="rent-upload-image-btn"
                >
                  {uploading ? <RefreshCw size={12} className="animate-spin" /> : <Upload size={12} />}
                  {uploading ? 'Uploading...' : 'Upload from device'}
                </button>
                <button type="button" onClick={addImage} className="text-xs text-blue-600 hover:underline" data-testid="add-rent-image">
                  + Add URL
                </button>
              </div>
            </div>
            <div className="space-y-2">
              {(data.images || []).map((img, i) => (
                <div key={i} className="flex gap-2 items-start">
                  <div className="flex-1">
                    <input value={img} onChange={e => updateImage(i, e.target.value)}
                      placeholder="https://... (paste image URL or upload above)"
                      className={input} data-testid={`rent-image-input-${i}`} />
                    {img && <img src={img} alt={`Preview ${i + 1}`} className="mt-1.5 w-20 h-20 object-contain bg-gray-50 rounded border" />}
                  </div>
                  <button type="button" onClick={() => removeImage(i)} className="p-2 text-red-400 hover:bg-red-50 rounded">
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
              {(data.images || []).length === 0 && <p className="text-xs text-gray-400 italic">No images yet. Click &quot;Upload from device&quot; to add product photos.</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminRentModels;
